import { useEffect, useRef } from 'react';
import { trackPageView, trackTimeOnSection, trackScrollDepth } from '../utils/analytics';

// Hook to track section visibility and time spent
export const useSectionTracking = (sectionName) => {
    const sectionRef = useRef();
    const startTimeRef = useRef();
    const hasBeenViewedRef = useRef(false);

    useEffect(() => {
        const currentSection = sectionRef.current;
        
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // Section is visible
                    startTimeRef.current = Date.now();
                    
                    if (!hasBeenViewedRef.current) {
                        trackPageView(`#${sectionName}`, `${sectionName} Section`);
                        hasBeenViewedRef.current = true;
                    }
                } else if (startTimeRef.current) {
                    // Section is no longer visible, track time spent
                    const timeSpent = Date.now() - startTimeRef.current;
                    if (timeSpent > 1000) { // Only track if viewed for more than 1 second
                        trackTimeOnSection(sectionName, timeSpent);
                    }
                    startTimeRef.current = null;
                }
            },
            {
                threshold: 0.5, // Trigger when 50% of the section is visible
                rootMargin: '0px 0px -50px 0px'
            }
        );

        if (currentSection) {
            observer.observe(currentSection);
        }

        return () => {
            if (currentSection) {
                observer.unobserve(currentSection);
                // Track final time if section was being viewed when component unmounts
                if (startTimeRef.current) {
                    const timeSpent = Date.now() - startTimeRef.current;
                    if (timeSpent > 1000) {
                        trackTimeOnSection(sectionName, timeSpent);
                    }
                }
            }
        };
    }, [sectionName]);

    return sectionRef;
};

// Hook to track scroll depth
export const useScrollTracking = () => {
    useEffect(() => {
        let maxScrollDepth = 0;
        const depthThresholds = [25, 50, 75, 90, 100];
        const trackedDepths = new Set();

        const handleScroll = () => {
            const scrollTop = window.pageYOffset;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const scrollPercent = Math.round((scrollTop / docHeight) * 100);
            
            if (scrollPercent > maxScrollDepth) {
                maxScrollDepth = scrollPercent;
            }

            // Track major scroll depth milestones
            depthThresholds.forEach(threshold => {
                if (scrollPercent >= threshold && !trackedDepths.has(threshold)) {
                    trackScrollDepth(threshold);
                    trackedDepths.add(threshold);
                }
            });
        };

        const throttledScroll = throttle(handleScroll, 100);
        window.addEventListener('scroll', throttledScroll);

        return () => {
            window.removeEventListener('scroll', throttledScroll);
        };
    }, []);
};

// Simple throttle utility
const throttle = (func, delay) => {
    let timeoutId;
    let lastExecTime = 0;
    
    return function (...args) {
        const currentTime = Date.now();
        
        if (currentTime - lastExecTime > delay) {
            func.apply(this, args);
            lastExecTime = currentTime;
        } else {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(this, args);
                lastExecTime = Date.now();
            }, delay - (currentTime - lastExecTime));
        }
    };
};